import { useState } from "react";
import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import type { ComparativeResultsType } from "../calculadora-comparativa";
import { calcularINSSBreakdown, calcularIRRFBreakdown, calcularFeriasCompleto } from "../utils/comparativeFunctions";

const formatarMoedaPtBR = (value: number): string => {
  return value.toFixed(2).replace('.', ',');
};

const Tooltip = ({ children, text }: { children: React.ReactNode, text: string }) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 max-w-xs text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-normal z-20 text-center">
      {text}
    </div>
  </div>
);

const INSSBreakdown = ({ salario }: { salario: number }) => {
  const breakdown = calcularINSSBreakdown(salario);

  type FaixaINSS = {
    limite: number;
    aliquota: number;
    valorFaixa: number;
    contribuicaoFaixa: number;
    limiteInicial: number;
    limiteFinal: number;
  };

  return (
    <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200 text-xs">
      <h5 className="font-semibold text-blue-800 mb-2">🧮 Cálculo INSS por Faixas:</h5>
      {breakdown.faixas.map((item: FaixaINSS, index: number) => (
        <p key={index} className="text-blue-700 mb-1">
          • {item.aliquota.toFixed(1)}% sobre R$ {item.valorFaixa.toFixed(2).replace('.', ',')} = R$ {item.contribuicaoFaixa.toFixed(2).replace('.', ',')}
        </p>
      ))}
      <div className="border-t border-blue-300 mt-2 pt-2">
        <p className="font-semibold text-blue-800">
          Total INSS: R$ {breakdown.valorTotal.toFixed(2).replace('.', ',')}
        </p>
      </div>
    </div>
  );
};

const IRRFBreakdown = ({ salario, inss, dependentes }: { salario: number, inss: number, dependentes: number }) => {
  const breakdown = calcularIRRFBreakdown(salario, inss, dependentes);

  return (
    <div className="mt-2 p-3 bg-orange-50 rounded border border-orange-200 text-xs">
      <h5 className="font-semibold text-orange-800 mb-2">🧮 Cálculo IRRF:</h5>
      <p className="text-orange-700">• Salário bruto: R$ {breakdown.salario.toFixed(2).replace('.', ',')}</p>
      <p className="text-orange-700">• (-) INSS: R$ {breakdown.inss.toFixed(2).replace('.', ',')}</p>
      {breakdown.dependentes > 0 && (
        <p className="text-orange-700">• (-) Dependentes ({breakdown.dependentes} × R$ 189,59): R$ {breakdown.deducaoDependentes.toFixed(2).replace('.', ',')}</p>
      )}
      <p className="text-orange-700 font-medium">• = Base de cálculo: R$ {breakdown.baseCalculo.toFixed(2).replace('.', ',')}</p>
      
      {breakdown.faixaAplicada.aliquota > 0 ? (
        <>
          <p className="text-orange-700 mt-1">• Faixa aplicada: {(breakdown.faixaAplicada.aliquota * 100).toFixed(1)}%</p>
          <p className="text-orange-700">• Cálculo: R$ {breakdown.baseCalculo.toFixed(2).replace('.', ',')} × {(breakdown.faixaAplicada.aliquota * 100).toFixed(1)}% - R$ {breakdown.faixaAplicada.parcelaDeducao.toFixed(2).replace('.', ',')}</p>
        </>
      ) : (
        <p className="text-orange-700 mt-1 font-medium">• Faixa: Isento (até R$ 2.428,80)</p>
      )}
      
      <div className="border-t border-orange-300 mt-2 pt-2">
        <p className="font-semibold text-orange-800">
          Total IRRF: R$ {breakdown.valorIRRF.toFixed(2).replace('.', ',')}
        </p>
      </div>
    </div>
  );
};

const FeriasBreakdown = ({ salario, dependentes }: { salario: number, dependentes: number }) => {
  const ferias = calcularFeriasCompleto(salario, dependentes);

  return (
    <div className="mt-2 p-3 bg-green-50 rounded border border-green-200 text-xs">
      <h5 className="font-semibold text-green-800 mb-2">Cálculo Férias + 1/3:</h5>
      <p className="text-green-700">• Salário de férias: R$ {ferias.salarioFerias.toFixed(2).replace('.', ',')}</p>
      <p className="text-green-700">• + 1/3 constitucional: R$ {ferias.umTercoFerias.toFixed(2).replace('.', ',')}</p>
      <p className="text-green-700 font-medium">• = Bruto férias: R$ {ferias.brutoFerias.toFixed(2).replace('.', ',')}</p>
      <p className="text-green-700">• (-) INSS sobre férias: R$ {ferias.inssFerias.toFixed(2).replace('.', ',')}</p>
      <p className="text-green-700">• (-) IRRF sobre férias: R$ {ferias.irrfFerias.toFixed(2).replace('.', ',')}</p>
      <div className="border-t border-green-300 mt-2 pt-2">
        <p className="font-semibold text-green-800">
          Férias líquidas: R$ {ferias.feriasLiquidas.toFixed(2).replace('.', ',')}
        </p>
      </div>
    </div>
  );
};

// Componente principal
export const ComparativeResponse = ({ results }: { results: ComparativeResultsType }) => {
  const { pjNecessario, cltResult } = results;
  
  const [showINSSDetail, setShowINSSDetail] = useState(false);
  const [showIRRFDetail, setShowIRRFDetail] = useState(false);
  const [showFeriasDetail, setShowFeriasDetail] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);

  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado da Comparação CLT x PJ</MainCardTitle>
      
      {/* Cards de Resumo Principal */}
      <div className="grid sm:grid-cols-3 gap-4 mb-2 grid-cols-1">
        <div className="p-3 bg-primary rounded border flex flex-col justify-center">
          <Tooltip text="Valor mensal que você precisa faturar como PJ para ter a mesma renda líquida do CLT, incluindo margem de segurança para sazonalidade">
            <h2 className="sm:text-base text-sm text-center text-white font-semibold m-0 cursor-help">
              Faturamento PJ Necessário/Mês
            </h2>
          </Tooltip>
          {pjNecessario.erro ? (
            <p className="sm:text-xl text-center font-bold text-md text-white m-0">
              Inviável
            </p>
          ) : (
            <p className="sm:text-xl text-center font-bold text-md text-white m-0">
              <span className="text-sm mr-1 text-white">R$</span>
              {normalSanatizerBRL(pjNecessario.recomendado)}
            </p>
          )}
        </div>
        
        <div className="p-3 bg-white rounded border flex flex-col justify-center">
          <Tooltip text="Valor mensal equivalente que você recebe no CLT: salário líquido + rateio anual do 13º, férias + 1/3, FGTS, benefícios empresariais e PLR (se houver)">
            <h2 className="sm:text-base text-sm text-center font-semibold m-0 cursor-help">
              CLT Equivalência/Mês
            </h2>
          </Tooltip>
          <p className="sm:text-lg text-center font-black text-md text-red-800 m-0">
            <span className="text-sm mr-1 text-gray-500">R$</span>
            {normalSanatizerBRL(cltResult.equivalenciaMensal)}
          </p>
        </div>
        
        <div className="p-3 bg-white rounded border flex flex-col justify-center">
          <Tooltip text="Percentual a mais que o PJ precisa faturar em relação à renda CLT para compensar a migração">
            <h2 className="sm:text-base text-sm text-center font-semibold m-0 cursor-help">
              Diferença Necessária
            </h2>
          </Tooltip>
          <p className="sm:text-lg text-center font-black text-md text-black m-0">
            {pjNecessario.erro ? 'N/A' : `+${formatarMoedaPtBR(results.percentualDiferenca)}%`}
          </p>
        </div>
      </div>

      {/* Status e Regime */}
      {!pjNecessario.erro && (
        <div className="mb-6 p-4 bg-gray-50 rounded border text-center">
          <p className="text-sm text-gray-600 mb-1">
            Regime: {pjNecessario.regime} ({formatarMoedaPtBR(pjNecessario.aliquota)}% de impostos)
          </p>
          <p className="text-sm text-gray-600">
            Faturamento {formatarMoedaPtBR(results.percentualDiferenca)}% maior que renda CLT
          </p>
        </div>
      )}

      {/* Erro PJ */}
      {pjNecessario.erro && (
        <div className="mb-6 p-4 bg-red-50 rounded border border-red-200 text-center">
          <h3 className="font-bold text-red-800 text-lg mb-2">
            {pjNecessario.regime} Inviável
          </h3>
          <p className="text-red-700 text-sm mb-2">{pjNecessario.mensagem}</p>
          <p className="text-red-600 text-sm">Considere o Simples Nacional</p>
        </div>
      )}

      <div className="mb-6">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="w-full p-3 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 text-left transition-colors duration-200"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-blue-800 text-sm">
              📊 Como Chegamos Nestes Valores
            </h3>
            <span className="text-blue-600 text-sm">
              {showMethodology ? '▼ Ocultar' : '▶ Ver Metodologia'}
            </span>
          </div>
        </button>
        
        {showMethodology && (
          <div className="mt-3 p-4 bg-blue-50 rounded border border-blue-200">
            <div className="grid sm:grid-cols-2 gap-4 text-xs">
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">CLT - Passo a Passo:</h4>
                <p className="text-blue-700 mb-1">1. Salário bruto: R$ {normalSanatizerBRL(cltResult.salarioBruto)}</p>
                <p className="text-blue-700 mb-1">2. (-) INSS por faixas: R$ {normalSanatizerBRL(cltResult.inss)}</p>
                <p className="text-blue-700 mb-1">3. (-) IRRF sobre base: R$ {normalSanatizerBRL(cltResult.irrf)}</p>
                {cltResult.salarioFamilia > 0 && (
                  <p className="text-blue-700 mb-1">4. (+) Salário-família: R$ {normalSanatizerBRL(cltResult.salarioFamilia)}</p>
                )}
                <p className="text-blue-700 mb-1 font-medium">= Líquido mensal: R$ {normalSanatizerBRL(cltResult.salarioLiquido)}</p>
                <p className="text-blue-700 mb-1">× 12 meses + 13º + férias + FGTS {cltResult.plr > 0 ? '+ PLR' : ''}</p>
                <p className="text-blue-700 font-semibold">= Total anual: R$ {normalSanatizerBRL(cltResult.totalAnual)}</p>
              </div>
              
              {!pjNecessario.erro && (
                <div>
                  <h4 className="font-semibold text-blue-800 mb-2">PJ - Passo a Passo:</h4>
                  <p className="text-blue-700 mb-1">1. Meta: igualar R$ {normalSanatizerBRL(cltResult.equivalenciaMensal)}/mês</p>
                  <p className="text-blue-700 mb-1">2. + Margem segurança: {pjNecessario.margemPercent}%</p>
                  <p className="text-blue-700 mb-1 font-medium">= Faturamento necessário: R$ {normalSanatizerBRL(pjNecessario.recomendado)}</p>
                  <p className="text-blue-700 mb-1">(-) Impostos regime: R$ {normalSanatizerBRL(pjNecessario.impostos)}</p>
                  {pjNecessario.proLabore > 0 && (
                    <p className="text-blue-700 mb-1">(-) Encargos pró-labore: R$ {normalSanatizerBRL(pjNecessario.inssProLabore + pjNecessario.irrfProLabore)}</p>
                  )}
                  <p className="text-blue-700 font-semibold">= Líquido estimado: R$ {normalSanatizerBRL(pjNecessario.recomendado - pjNecessario.impostos - pjNecessario.inssProLabore - pjNecessario.irrfProLabore)}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Breakdown CLT */}
      <h2 className="font-bold text-primary text-lg mt-8 text-center">
        Detalhamento CLT:
      </h2>
      <div className="border rounded p-4 mb-6">
        <div className="grid sm:grid-cols-2 gap-4 mb-4 grid-cols-1">
          <div className="p-3 bg-gray-50 rounded border">
            <Tooltip text="Salário mensal após desconto de INSS, IRRF e acréscimo de salário-família (se aplicável)">
              <h3 className="text-sm font-semibold text-center mb-1 cursor-help">Salário Líquido Mensal</h3>
            </Tooltip>
            <p className="text-lg font-bold text-center text-gray-800">
              R$ {normalSanatizerBRL(cltResult.salarioLiquido)}
            </p>
            <p className="text-xs text-center text-gray-600">Por mês</p>
          </div>
          
          <div className="p-3 bg-gray-50 rounded border">
            <Tooltip text="Soma de todos os valores anuais: 12 salários líquidos + 13º + férias + FGTS + benefícios + PLR">
              <h3 className="text-sm font-semibold text-center mb-1 cursor-help">Renda Total Anual</h3>
            </Tooltip>
            <p className="text-lg font-bold text-center text-gray-800">
              R$ {normalSanatizerBRL(cltResult.totalAnual)}
            </p>
            <p className="text-xs text-center text-gray-600">Tudo que você recebe no ano</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
          <div className="p-3 bg-white rounded border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Descontos Mensais:</h4>
            
            {/* INSS com detalhamento */}
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">INSS: R$ {normalSanatizerBRL(cltResult.inss)}</span>
                <button 
                  onClick={() => setShowINSSDetail(!showINSSDetail)}
                  className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {showINSSDetail ? '▼' : '🧮'}
                </button>
              </div>
              {showINSSDetail && <INSSBreakdown salario={cltResult.salarioBruto} />}
            </div>

            {/* IRRF com detalhamento */}
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">IRRF: R$ {normalSanatizerBRL(cltResult.irrf)}</span>
                <button 
                  onClick={() => setShowIRRFDetail(!showIRRFDetail)}
                  className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {showIRRFDetail ? '▼' : '🧮'}
                </button>
              </div>
              {showIRRFDetail && <IRRFBreakdown salario={cltResult.salarioBruto} inss={cltResult.inss} dependentes={0} />}
            </div>

            {cltResult.salarioFamilia > 0 && (
              <Tooltip text="R$ 65,00 por dependente menor de 14 anos. Pago apenas para salários até R$ 1.906,04">
                <p className="text-sm text-green-600 cursor-help">Sal. Família: +R$ {normalSanatizerBRL(cltResult.salarioFamilia)}</p>
              </Tooltip>
            )}
          </div>
          
          <div className="p-3 bg-white rounded border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Benefícios Anuais:</h4>
            
            <Tooltip text="Mesmo valor do salário líquido mensal, calculado com mesma tributação">
              <p className="text-sm text-gray-600 cursor-help mb-1">13º Líquido: R$ {normalSanatizerBRL(cltResult.decimoTerceiro)}</p>
            </Tooltip>
            
            {/* Férias com detalhamento */}
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Férias + 1/3: R$ {normalSanatizerBRL(cltResult.ferias)}</span>
                <button 
                  onClick={() => setShowFeriasDetail(!showFeriasDetail)}
                  className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {showFeriasDetail ? '▼' : '🧮'}
                </button>
              </div>
              {showFeriasDetail && <FeriasBreakdown salario={cltResult.salarioBruto} dependentes={0} />}
            </div>

            <Tooltip text="8% do salário bruto sobre 12 meses + 13º + férias. É um fundo de direito do trabalhador">
              <p className="text-sm text-gray-600 cursor-help mb-1">FGTS (8%): R$ {normalSanatizerBRL(cltResult.fgts)}</p>
            </Tooltip>
            
            {cltResult.plr > 0 && (
              <Tooltip text="PLR com tributação própria: isenta até R$ 7.640,80, depois tabela progressiva de 7,5% a 27,5%">
                <p className="text-sm text-green-600 cursor-help mb-1">PLR Líquida: R$ {normalSanatizerBRL(cltResult.plr)}</p>
              </Tooltip>
            )}
            
            {cltResult.beneficiosAnuais > 0 && (
              <Tooltip text="Vale alimentação, transporte, plano saúde e outros benefícios somados anualmente">
                <p className="text-sm text-gray-600 cursor-help">Outros Benefícios: R$ {normalSanatizerBRL(cltResult.beneficiosAnuais)}</p>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Breakdown PJ */}
      {!pjNecessario.erro && (
        <>
          <h2 className="font-bold text-primary text-lg my-6 text-center">
            Detalhamento PJ ({pjNecessario.regime}):
          </h2>
          <div className="border rounded p-4 mb-6">
            <div className="grid sm:grid-cols-2 gap-4 mb-4 grid-cols-1">
              <div className="p-3 bg-gray-50 rounded border">
                <Tooltip text="Faturamento mensal recomendado incluindo margem de segurança para cobrir sazonalidade, inadimplência e imprevistos">
                  <h3 className="text-sm font-semibold text-center mb-1 cursor-help">Faturamento Recomendado</h3>
                </Tooltip>
                <p className="text-lg font-bold text-center text-gray-800">
                  R$ {normalSanatizerBRL(pjNecessario.recomendado)}
                </p>
                <p className="text-xs text-center text-gray-600">Com {pjNecessario.margemPercent}% de margem</p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded border">
                <Tooltip text="Estimativa do valor líquido final que sobra após todos os impostos PJ e encargos do pró-labore">
                  <h3 className="text-sm font-semibold text-center mb-1 cursor-help">Renda Líquida Estimada</h3>
                </Tooltip>
                <p className="text-lg font-bold text-center text-gray-800">
                  R$ {normalSanatizerBRL(pjNecessario.recomendado - pjNecessario.impostos - pjNecessario.inssProLabore - pjNecessario.irrfProLabore)}
                </p>
                <p className="text-xs text-center text-gray-600">
                  {pjNecessario.proLabore > 0 ? 'Pró-labore + Distribuição' : 'Distribuição de lucros'}
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
              <div className="p-3 bg-white rounded border">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Custos Mensais PJ:</h4>
                <Tooltip text={`Impostos do regime ${pjNecessario.regime}: ${formatarMoedaPtBR(pjNecessario.aliquota)}% sobre o faturamento`}>
                  <p className="text-sm text-gray-600 cursor-help mb-1">Impostos PJ: R$ {normalSanatizerBRL(pjNecessario.impostos)}</p>
                </Tooltip>
                
                {pjNecessario.proLabore > 0 && (
                  <>
                    <Tooltip text="INSS sobre o pró-labore para garantir direitos previdenciários (aposentadoria)">
                      <p className="text-sm text-gray-600 cursor-help mb-1">INSS Pró-labore: R$ {normalSanatizerBRL(pjNecessario.inssProLabore)}</p>
                    </Tooltip>
                    {pjNecessario.irrfProLabore > 0 && (
                      <Tooltip text="Imposto de Renda retido na fonte sobre o pró-labore">
                        <p className="text-sm text-gray-600 cursor-help">IRRF Pró-labore: R$ {normalSanatizerBRL(pjNecessario.irrfProLabore)}</p>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
              
              <div className="p-3 bg-white rounded border">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Informações:</h4>
                {pjNecessario.proLabore > 0 && (
                  <Tooltip text="Remuneração obrigatória do sócio, necessária para contribuição previdenciária">
                    <p className="text-sm text-gray-600 cursor-help mb-1">Pró-labore: R$ {normalSanatizerBRL(pjNecessario.proLabore)}</p>
                  </Tooltip>
                )}
                <Tooltip text="Faturamento mínimo necessário sem margem de segurança">
                  <p className="text-sm text-gray-600 cursor-help mb-1">Fatur. Mínimo: R$ {normalSanatizerBRL(pjNecessario.minimo)}</p>
                </Tooltip>
                <Tooltip text="Percentual médio de impostos sobre o faturamento neste regime">
                  <p className="text-sm text-gray-600 cursor-help">Alíquota Média: {formatarMoedaPtBR(pjNecessario.aliquota)}%</p>
                </Tooltip>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Análise Qualitativa */}
      <h2 className="font-bold text-primary text-lg my-6 text-center">
        Análise Qualitativa:
      </h2>
      <div className="border rounded p-4 mb-6">
        <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
          <div className="p-3 bg-green-50 rounded border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3 text-sm">✓ Vantagens PJ</h4>
            <ul className="space-y-1">
              {results.analise.vantagens.map((vantagem: string, index: number) => (
                <li key={index} className="text-xs text-green-700">
                  • {vantagem}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-3 bg-red-50 rounded border border-red-200">
            <h4 className="font-semibold text-red-800 mb-3 text-sm">✗ Desvantagens PJ</h4>
            <ul className="space-y-1">
              {results.analise.desvantagens.map((desvantagem: string, index: number) => (
                <li key={index} className="text-xs text-red-700">
                  • {desvantagem}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded border">
          <h4 className="font-semibold text-gray-800 mb-2 text-sm">💡 Recomendação Final:</h4>
          <p className="text-sm text-gray-700">{results.analise.recomendacao}</p>
        </div>
      </div>

      {/* Guia de Regimes */}
      <h2 className="font-bold text-primary text-lg my-6 text-center">
        Guia dos Regimes Tributários:
      </h2>
      <div className="grid sm:grid-cols-3 gap-4 grid-cols-1">
        <div className="p-3 bg-white rounded border">
          <h3 className="font-semibold text-gray-800 text-sm mb-2">MEI</h3>
          <p className="text-xs text-gray-600 mb-1">Limite: R$ 81.000/ano</p>
          <p className="text-xs text-gray-600 mb-1">Impostos: R$ 76,90 a R$ 81,90/mês</p>
          <p className="text-xs text-gray-600">Pró-labore: Não obrigatório</p>
        </div>
        
        <div className="p-3 bg-white rounded border">
          <h3 className="font-semibold text-gray-800 text-sm mb-2">Simples Nacional</h3>
          <p className="text-xs text-gray-600 mb-1">R$ 4.800.000/ano</p>
          <p className="text-xs text-gray-600 mb-1">Impostos: IRPJ, CSLL, PIS/COFINS + ICMS/ISS</p>
          <p className="text-xs text-gray-600">Pró-labore: Obrigatório</p>
        </div>
        
        <div className="p-3 bg-white rounded border">
          <h3 className="font-semibold text-gray-800 text-sm mb-2">Lucro Presumido</h3>
          <p className="text-xs text-gray-600 mb-1">Sem limite de faturamento</p>
          <p className="text-xs text-gray-600 mb-1">TImpostos: 11,33% a 16,33% conforme atividade</p>
          <p className="text-xs text-gray-600">Pró-labore: Obrigatório</p>
        </div>
      </div>

      {/* Disclaimer expandido */}
      <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
        <p className="text-sm text-yellow-800">
          <strong>Importante:</strong> Esta é uma simulação com valores aproximados usando tabelas tributárias 2025. Não considera custos operacionais PJ (contador R$ 300-800/mês, certificado digital, software) e variações específicas de cada regime. PLR acima de R$ 7.640,80 usa tabela progressiva própria. Sempre consulte um contador para análise personalizada.
        </p>
      </div>

      {/* Info PLR quando aplicável */}
      {cltResult.plr > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
          <h4 className="font-semibold text-blue-800 mb-1 text-sm">💰 Sobre a PLR:</h4>
          <p className="text-xs text-blue-700">
            A PLR de <strong>R$ {normalSanatizerBRL(cltResult.plr)}</strong> foi considerada no cálculo CLT com tributação própria (isenta até R$ 7.640,80). Como PJ, você não receberá PLR, mas pode ter maior potencial de ganhos com múltiplos clientes e preços mais altos.
          </p>
        </div>
      )}
    </MainCard>
  );
};